import { useEffect, useState } from "react";
import ToggleSwitch from "../../components/ToggleSwitch";
import VerificationButtonLink from "./../../components/VerificationButtonLink";
import { BsCashStack } from "react-icons/bs";
import { FaCreditCard } from "react-icons/fa6";
import { GiMoneyStack } from "react-icons/gi";
import {
  BusinessVerificationStatus,
  PaymentMethods,
} from "@/types/globalTypes";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import ErrorMessage from "@/components/ErrorMessage";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { isProduction } from "@/utils/helpers";

function PaymentSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  
  const [payementMethods, setPaymentMethods] = useState<
    Omit<PaymentMethods, "businessVerificationStatus">
  >({} as PaymentMethods);
  const [businessVerificationStatus, setBusinessVerificationStatus] =
    useState<BusinessVerificationStatus>("not-verified"); 
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [needRefetch, setNeedRefetch] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const url = `${BASE_URL}/api/dashboard/settings/payment?locale=${lang}`;

  useEffect(() => {
    if(!token) return

    setIsLoading(true);
    setNeedRefetch(false);
    setIsRequesting(false);

    const fetchPaymentMethods = async () => {
      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (response.data.success) {
          setPaymentMethods({
            onlineActivated: response.data?.onlineActivated,
            cashActivated: response.data?.cashActivated,
          });
          setBusinessVerificationStatus(
            response.data.businessVerificationStatus,
          );
        } else {
          const error =
            response.data.error ||
            (toast.error(t("paymentSettings.unexpectedError") as string));
          toast.error(error);
          setError(error);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            (t("paymentSettings.unexpectedError") as string);
          toast.error(message);
          setError(message);
        } else {
          console.error(error);
          toast.error(
            t("paymentSettings.unexpectedError") as string,
          );
          setError(
            t("paymentSettings.unexpectedError") as string,
          );
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchPaymentMethods();
  }, [needRefetch, t, token, url]);

  async function updatePaymentMethods(
    bodyField: keyof Omit<PaymentMethods, "businessVerificationStatus">,
    value: boolean,
  ) {
    // Prevent deactivation of the only active payment method
    const activeMethodsCount = Object.values(payementMethods).filter(
      (isActive) => isActive,
    ).length;

    if (!value && activeMethodsCount === 1 && payementMethods[bodyField]) {
      toast.error(
        t("paymentSettings.cannotDeactivateLastMethod") as string,
      );
      return;
    }

    const isOnlinePayment = bodyField === "onlineActivated";
    if (isOnlinePayment && value && businessVerificationStatus !== "verified") {
      toast.error(
       t("paymentSettings.verifyStoreBeforeOnlinePayment") as string
      );
      return;
    }

    const LoadingTaost = toast.loading(
      t("paymentSettings.updatingMethods") as string,
    );
    setIsRequesting(true);

    try {
      const response = await axios.put(
        url,
        {
          ...payementMethods,
          [bodyField]: value,
        },
        {
          headers: {
            Authorization: `${token}`,
          },
        },
      );

      if (response.data.success) {
        toast.success(
            t("paymentSettings.updateSuccess" ) as string,
        );
        setNeedRefetch(true);
      } else {
        const error =
          response.data.error ||
          (t("paymentSettings.unexpectedError") as string);
          toast.error(error);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          (t("paymentSettings.unexpectedError") as string);
          toast.error(message);
      } else {
        console.error(error);
        toast.error(
            t("paymentSettings.unexpectedError") as string
        );
      }
    } finally {
      toast.dismiss(LoadingTaost);
      setIsRequesting(false);
    }
  }

  if (isLoading)
    return (
      <>
        <Skeleton className="h-[100px] w-full px-5 dark:bg-slate-600 dark:bg-opacity-35" />
        <Skeleton className="mt-20 h-[100px] w-full px-5 dark:bg-slate-600 dark:bg-opacity-35" />
        <Skeleton className="mt-10 h-[100px] w-full px-5 dark:bg-slate-600 dark:bg-opacity-35" />
        <Skeleton className="mt-10 h-[100px] w-full px-5 dark:bg-slate-600 dark:bg-opacity-35" />
      </>
    );

  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col">
      <VerificationButtonLink verificationStatus={businessVerificationStatus} />

      <h1 className="mb-3 text-xl">
        {t("paymentSettings.chooseMethods")}
      </h1>

          {/* Payment Methods */}
      <div className="flex flex-col gap-5">
        {/* Online payment */}
        
        {isProduction ? null : (        <div className="flex flex-col gap-3">
          <h2 className="font-semibold">
            {t("paymentSettings.onlinePayment")}
          </h2>

          <div className="main-border flex flex-col gap-5 px-3 py-4">
            
            <div className="flex items-center gap-3">
              <FaCreditCard size={25} />

              <h2 className="text-xl">
                {t( "paymentSettings.cardAndApplePay" )}
              </h2>
            </div>

            <div className="flex w-full items-center justify-between pe-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="h-[65px] w-[80px]">
                  <img
                    className="size-full"
                    src="/logos/masterCardLogo.png"
                    alt="Master Card"
                  />
                </div>
                <div className="mb-3 h-[33px] w-[100px]">
                  <img
                    className="size-full"
                    src="/logos/VisaLogo.png"
                    alt="Visa"
                  />
                </div>
                <div className="mb-2 h-[50px] w-[100px]">
                  <img
                    className="size-full"
                    src="/logos/ApplePayLogo.svg"
                    alt="Apple Pay"
                  />
                </div>
              </div>

              <ToggleSwitch
                disabled={isLoading || isRequesting}
                size="small"
                checked={payementMethods.onlineActivated}
                onChange={() =>
                  updatePaymentMethods(
                    "onlineActivated",
                    !payementMethods.onlineActivated,
                  )
                }
              />
            </div>
          </div>

          
        </div>)}

        <div className="flex flex-col gap-3">
          <h2 className="font-semibold">
            {t("paymentSettings.cashPayment")}
          </h2>

          {/* Cash */}
          <div className="main-border flex flex-col gap-5 px-3 py-4">
            <div className="flex items-center gap-3">
              <BsCashStack size={25} />

              <h2 className="text-xl">{t("paymentSettings.cash")}</h2>
            </div>

            <div className="flex w-full items-center justify-between pe-4 ps-2">
              <GiMoneyStack size={60} className="text-success" />

              <ToggleSwitch
                disabled={isLoading || isRequesting}
                size="small"
                checked={payementMethods.cashActivated}
                onChange={() =>
                  updatePaymentMethods(
                    "cashActivated",
                    !payementMethods.cashActivated,
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentSettings;
